import React, { useState, useEffect } from "react";
import { Table, Button, Modal } from "antd";
import { Link, useLocation } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { getAllDocuments, addDocument } from "../../../firebase/dbService";
import { getFileURL } from "../../../firebase/storageService";
import { plusicon, refreshicon, calendar } from "../../../Components/imagepath";
import { onShowSizeChange, itemRender } from "../../../Components/Pagination";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Header from "../../../Components/Header";
import Sidebar from "../../../Components/Sidebar";

const InformationCardList = () => {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const updateSuccess = sessionStorage.getItem("updateInformationCardSuccess");
    const addSuccess = sessionStorage.getItem("addInformationCardSuccess");
    if (updateSuccess) {
      toast.success("Document updated successfully!", { autoClose: 2000 });
      sessionStorage.removeItem("updateInformationCardSuccess");
    }
    if (addSuccess) {
      toast.success("Document Added successfully!", { autoClose: 2000 });
      sessionStorage.removeItem("addInformationCardSuccess");
    }
    fetchData();
  }, [location]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getAllDocuments('informationCard');
      let data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (data.length === 0) {
        const dummyData = [
          {
            Title: "Sample Event 1",
            Description: "<p>Sample Event Description 1</p>",
            imageUrl: "",
            displayImageUrl: "https://via.placeholder.com/150x100/4CAF50/FFFFFF?text=Event+1",
            streetAddress: "Musterstraße",
            houseNumber: "123",
            postalCode: "10115",
            city: "Berlin",
            state: "Berlin",
            country: "Deutschland",
            eventDate: "2024-12-15",
            eventTime: "18:00",
            contact: "contact1@example.com"
          },
          {
            Title: "Sample Event 2",
            Description: "<p>Sample Event Description 2</p>",
            imageUrl: "",
            displayImageUrl: "https://via.placeholder.com/150x100/2196F3/FFFFFF?text=Event+2",
            streetAddress: "Beispielweg",
            houseNumber: "456",
            postalCode: "80331",
            city: "München",
            state: "Bayern",
            country: "Deutschland",
            eventDate: "2024-12-20",
            eventTime: "19:30",
            contact: "contact2@example.com"
          },
          {
            Title: "Sample Event 3",
            Description: "<p>Sample Event Description 3</p>",
            imageUrl: "",
            displayImageUrl: "https://via.placeholder.com/150x100/FF9800/FFFFFF?text=Event+3",
            streetAddress: "Teststraße",
            houseNumber: "789",
            postalCode: "50667",
            city: "Köln",
            state: "Nordrhein-Westfalen",
            country: "Deutschland",
            eventDate: "2024-12-25",
            eventTime: "16:00",
            contact: "contact3@example.com"
          },
        ];

        for (const dummy of dummyData) {
          const { displayImageUrl, ...dataToSave } = dummy; // Remove displayImageUrl from database save
          const newDocRef = await addDocument('informationCard', dataToSave);
          data.push({
            id: newDocRef.id,
            ...dummy // But keep it in the local data for immediate display
          });
        }
      }

      // Load image URLs for each item
      const dataWithImages = await Promise.all(
        data.map(async (item) => {
          console.log('Processing item:', item.id, 'imageUrl:', item.imageUrl, 'existing displayImageUrl:', item.displayImageUrl);

          // If item already has a displayImageUrl (like dummy data), keep it
          if (item.displayImageUrl) {
            return item;
          }

          if (item.imageUrl && item.imageUrl.trim() !== '') {
            try {
              const imageUrl = await getFileURL(item.imageUrl);
              console.log('Successfully loaded image URL:', imageUrl);
              return { ...item, displayImageUrl: imageUrl };
            } catch (error) {
              console.error(`Error loading image for ${item.id}:`, error);
              return { ...item, displayImageUrl: null };
            }
          }
          return { ...item, displayImageUrl: null };
        })
      );

      setDataSource(dataWithImages);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error fetching data: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (imageUrl) => {
    console.log('Image clicked:', imageUrl);
    setSelectedImage(imageUrl);
    setImageModalVisible(true);
  };

  const columns = [
    {
      title: "S/N",
      dataIndex: "serialNumber",
      key: "serialNumber",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Event Image",
      dataIndex: "displayImageUrl",
      key: "displayImageUrl",
      render: (imageUrl, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={calendar}
            alt="calendar"
            style={{ width: '24px', height: '24px', marginRight: '8px' }}
          />
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Event"
              style={{
                width: '50px',
                height: '50px',
                objectFit: 'cover',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              onClick={() => handleImageClick(imageUrl)}
            />
          ) : (
            <div
              style={{
                width: '50px',
                height: '50px',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: '#666'
              }}
            >
              No Image
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Event Title",
      dataIndex: "Title",
      key: "Title",
    },
    {
      title: "Description",
      dataIndex: "Description",
      key: "Description",
      render: (text) => (
        <div
          className="multiline-text"
          dangerouslySetInnerHTML={{ __html: text.substring(0, 30) + (text.length > 30 ? "..." : "") }}
        />
      ),
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (text, record) => {
        const address = record.streetAddress && record.city
          ? `${record.streetAddress} ${record.houseNumber || ''}, ${record.postalCode} ${record.city}`.trim()
          : "Not set";
        return (
          <div className="text-wrap" style={{ maxWidth: '150px' }}>
            {address.length > 30 ? `${address.substring(0, 30)}...` : address}
          </div>
        );
      },
    },
    {
      title: "Date & Time",
      dataIndex: "datetime",
      key: "datetime",
      render: (text, record) => {
        const dateTime = record.eventDate && record.eventTime
          ? `${record.eventDate} um ${record.eventTime}`
          : record.eventDate || record.eventTime || "Not set";
        return (
          <div className="text-wrap" style={{ maxWidth: '120px' }}>
            {dateTime.length > 25 ? `${dateTime.substring(0, 25)}...` : dateTime}
          </div>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <div className="text-end">
          <div className="dropdown dropdown-action">
            <Link
              to="#"
              className="action-icon dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="fas fa-ellipsis-v" />
            </Link>
            <div className="dropdown-menu dropdown-menu-end">
              <Link
                className="dropdown-item"
                to={`/informationcard/editinformationcard/${record.id}`}
              >
                <i className="far fa-edit me-2" />
                Edit Event
              </Link>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const handleRefresh = () => {
    fetchData();
  };

  return (
    <>
      <Header />
      <Sidebar
        id="menu-item4"
        id1="menu-items4"
        activeClassName="informationcard"
      />
      <div className="page-wrapper">
        <div className="content">
          {/* Page Navbar */}
          <div className="settings-menu-links">
            <ul className="nav nav-tabs menu-tabs">
              <li className="nav-item">
                <Link
                  className="nav-link"
                  to="/eventsheader"
                >
                  Events Header
                </Link>
              </li>
              <li className="nav-item active">
                <Link
                  className="nav-link"
                  to="/informationcard"
                >
                  Events Management
                </Link>
              </li>
            </ul>
          </div>
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="#">Events Section</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FeatherIcon icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">
                    Events Management
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12">
              <div className="card card-table show-entire border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                <div className="card-body p-4">
                  <div className="page-table-header mb-4">
                    <div className="row align-items-center">
                      <div className="col">
                        <div className="table-toolbar">
                          <h3 className="fw-bold m-0" style={{ color: '#1a202c', fontSize: '22px' }}>Events Management</h3>
                        </div>
                      </div>
                      <div className="col-auto ms-auto">
                        <div className="page-btn">
                          <Link
                            to="/informationcard/addinformationcard"
                            className="btn btn-primary shadow-sm d-flex align-items-center justify-content-center"
                            style={{ borderRadius: '8px', padding: '10px 20px', fontWeight: 600, border: 'none' }}
                          >
                            <img src={plusicon} alt="add" className="me-2 filter-invert" style={{ width: '16px', height: '16px' }} />
                            Add Event
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="table-responsive table-list">
                    <Table
                      loading={loading}
                      pagination={{
                        total: dataSource.length,
                        showTotal: (total, range) =>
                          `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                        onShowSizeChange: onShowSizeChange,
                        itemRender: itemRender,
                      }}
                      columns={columns}
                      dataSource={dataSource}
                      rowKey={(record) => record.id}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      <Modal
        title="Event Image"
        open={imageModalVisible}
        onCancel={() => setImageModalVisible(false)}
        footer={null}
        width={600}
        centered
      >
        {selectedImage && (
          <img
            src={selectedImage}
            alt="Event"
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '500px',
              objectFit: 'contain'
            }}
          />
        )}
      </Modal>

      <ToastContainer />
    </>
  );
};

export default InformationCardList;
